// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

// D-CRX-89 — retire one administration protocol, after a warning that names every drug still using it.
// Mounted by the drug record's protocol slot AND the CareRx Admin protocols library (L-067: one unit, two
// surfaces). Retire never deletes; with drugs still on it the server unassigns them (unassign: true).

import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import ConfirmDialog from '../../../components/ui/ConfirmDialog'
import { useToast } from '../../../components/ui/Toast'
import type { AdminProtocol } from '../../../api/client'
import { friendlyError } from '../../../api/errors'
import { useProtocolUsage, useRetireProtocol } from '../../../hooks/queries/carerxProtocols'
import ProtocolImpact from './ProtocolImpact'

export default function ProtocolRetireDialog({ protocol, onClose, onRetired }: {
  /** The protocol to retire; null = closed. */
  protocol: AdminProtocol | null
  onClose: () => void
  onRetired?: () => void
}) {
  const toast = useToast()
  const retire = useRetireProtocol()
  const usage = useProtocolUsage(protocol?.id ?? null)
  const drugNames = (usage.data?.drugs ?? []).map(d => d.name).join(', ')

  const doRetire = () => {
    if (!protocol) return
    retire.mutate({ id: protocol.id, unassign: true }, {
      onSuccess: () => { toast.show(`${protocol.name} retired.`, 'success'); onRetired?.(); onClose() },
      onError: e => toast.show(friendlyError(e, 'The protocol could not be retired.'), 'error'),
    })
  }

  return (
    <ConfirmDialog open={protocol !== null} tone="danger" busy={retire.isPending} maxWidth="sm"
      title={`Retire ${protocol?.name ?? 'this protocol'}?`} confirmLabel="Retire" busyLabel="Retiring…"
      confirmDisabled={usage.isLoading || usage.isError}
      error={usage.isError ? friendlyError(usage.error, 'Could not load the drugs that use this protocol.') : null}
      onCancel={onClose} onConfirm={doRetire}
      body={
        <Stack spacing={1} data-testid="protocol-retire-warning">
          {usage.isLoading && <Box>Checking everything this protocol reaches…</Box>}
          {usage.data && (
            <>
              {usage.data.drugs.length > 0
                ? <Box>Still used by <b>{drugNames}</b>. Retiring removes it from {usage.data.drugs.length === 1 ? 'that drug' : 'those drugs'}.</Box>
                : <Box>No drug uses it.</Box>}
              <ProtocolImpact usage={usage.data} action="retire" />
            </>
          )}
          <Box>It is never deleted. If this is not acceptable, cancel and pick or create another protocol for the drug instead.</Box>
        </Stack>
      } />
  )
}
